import { set } from 'lodash';
import { create, all, bignumber } from 'mathjs';
import { createSelector } from 'reselect';

import { accountsByIdSelector } from 'selectors/accounts';
import { groupsByIdSelector } from 'selectors/groups';
import groupRowsSelector from 'selectors/groupRows';
import { periodColumnsForKpiCalculationsSelector } from 'selectors/periods';
import { consolidationsSelector } from 'selectors/consolidations';

import { ContentType } from 'constants/types';
import { KpiCalculationStatus, KpiCalculationResultType } from 'constants/kpis';

import { getGroupSubsidiaryPrefix } from 'utils/groups';

export const kpiGroupsIdsSelector = (state) => state.kpi.groupIds;
export const rawKpiEntriesByIdSelector = (state) => state.kpi.entriesById;

// Extract contentDescription directly from 'groups' or 'accounts'
// because those can be changed
export const kpiEntriesByIdSelector = createSelector(
  rawKpiEntriesByIdSelector,
  accountsByIdSelector,
  groupsByIdSelector,
  consolidationsSelector,
  (entriesById, accountsById, groupsById, consolidations) => {
    const entriesByIdWithContentDescription = {};

    Object.keys(entriesById).forEach((entryId) => {
      const entry = entriesById[entryId];
      const parameterAssignmentsWithContentDescription = {};

      if (entry.parameterAssignments) {
        Object.keys(entry.parameterAssignments).forEach(
          (parameterAssignmentId) => {
            const parameterAssignment =
              entry.parameterAssignments[parameterAssignmentId];

            let subPrefix = '';
            if (parameterAssignment.contentType === 'group') {
              const hasGroup = parameterAssignment.contentId in groupsById;
              const group =
                hasGroup && groupsById[parameterAssignment.contentId];
              if (hasGroup) {
                subPrefix = `${getGroupSubsidiaryPrefix(
                  group,
                  consolidations
                )} `;
              }
            }

            const contentDescription =
              parameterAssignment.contentType === 'account'
                ? parameterAssignment.contentId in accountsById
                  ? accountsById[parameterAssignment.contentId].description
                  : 'Account does not exist'
                : parameterAssignment.contentId in groupsById
                ? `${subPrefix}${
                    groupsById[parameterAssignment.contentId].name
                  }`
                : 'Group does not exist';

            parameterAssignmentsWithContentDescription[
              parameterAssignmentId
            ] = {
              ...parameterAssignment,
              contentDescription,
            };
          }
        );
      }

      entriesByIdWithContentDescription[entryId] = {
        ...entry,
        parameterAssignments: parameterAssignmentsWithContentDescription,
      };
    });

    return entriesByIdWithContentDescription;
  }
);
export const kpiGroupsByIdSelector = (state) => state.kpi.groupsById;

export const kpiGroupsSelector = createSelector(
  kpiGroupsByIdSelector,
  (kpiGroupsById) => {
    return Object.keys(kpiGroupsById).map((id) => ({
      id,
      ...kpiGroupsById[id],
    }));
  }
);

const kpiCalculations = (kpiEntry, kpiPeriods, groupRows) => {
  const { parameterAssignments } = kpiEntry;
  const parameterAssignmentsValues = Object.values(parameterAssignments);

  const resultColumns = [];
  const parameterByColumnField = {};

  const result = {};

  // Get list of all periods needed in the result
  kpiPeriods.forEach((kpiPeriodColumn) => {
    if (kpiPeriodColumn.field) {
      resultColumns.push(kpiPeriodColumn);
    }

    const hasChildren =
      kpiPeriodColumn.children && kpiPeriodColumn.children.length;
    const children = hasChildren ? kpiPeriodColumn.children : [];

    children.forEach((child) => {
      resultColumns.push(child);
    });
  });

  const math = create(all, {
    number: 'BigNumber',
    precision: 64,
  });
  const parser = math.parser();

  parameterAssignmentsValues.forEach((parameterAssignment) => {
    const { contentId, contentType, parameter } = parameterAssignment;

    let groupRow = null;

    switch (contentType) {
      case ContentType.group:
        groupRow = groupRows.find((row) => row.group && row.id === contentId);
        break;
      case ContentType.account:
        groupRow = groupRows.find((row) => !row.group && row.id === contentId);
        break;
      default:
        groupRow = null;
        break;
    }

    const hasGroupRow = !!groupRow;
    if (!hasGroupRow) {
      return;
    }

    Object.values(resultColumns).forEach((column) => {
      let valueToDisplay = null;
      // Fields which already exist in groupRows, such as 'unadjusted'
      if (column.field in groupRow) {
        valueToDisplay = groupRow[column.field].value;
      }
      // Fields which are calculated by valueGetters, such as 'net' and 'grand total'
      else if (column.valueGetter) {
        valueToDisplay = column.valueGetter({ data: groupRow }).value;
      }
      set(
        parameterByColumnField,
        `${column.field}.${parameter}`,
        valueToDisplay
      );
    });
  });

  Object.keys(parameterByColumnField).forEach((columnField) => {
    Object.keys(parameterByColumnField[columnField]).forEach((parameter) => {
      parser.set(
        parameter,
        bignumber(parameterByColumnField[columnField][parameter])
      );
    });

    const hasPeriodDaysParameter = !!kpiEntry.periodDaysParameter;
    const column = resultColumns.find(
      (column) => !!column.field && column.field === columnField
    );
    const periodDays = column && parseInt(column.numberOfDays, 10);

    if (hasPeriodDaysParameter && periodDays) {
      parser.set(kpiEntry.periodDaysParameter, bignumber(periodDays));
    }

    let evaluationResult = {
      type: KpiCalculationResultType.result,
      result: '',
    };
    try {
      evaluationResult.result = parser.evaluate(kpiEntry.equation);
      if (!evaluationResult.result.isFinite()) {
        evaluationResult = {
          type: KpiCalculationResultType.error,
          status: KpiCalculationStatus.invalidParameterValues,
        };
      }
    } catch (error) {
      evaluationResult = {
        type: KpiCalculationResultType.error,
        status: KpiCalculationStatus.invalidExpression,
      };
    }

    result[columnField] = evaluationResult;
    parser.clear();
  });

  return result;
};

const getKpiRows = (
  kpiEntriesById,
  kpiGroupsById,
  kpiGroupsIds,
  kpiPeriods,
  groupRows
) => {
  let rows = [];

  kpiGroupsIds.forEach((kpiGroupId) => {
    const kpiGroup = kpiGroupsById[kpiGroupId];
    const kpiGroupPath = `${kpiGroupId}-${kpiGroup.name}`;

    rows.push({
      id: kpiGroupId,
      name: kpiGroup.name,
      childrenIds: kpiGroup.childrenIds,
      group: true,
      contentType: ContentType.kpiGroup,
      groupPath: [kpiGroupPath],
      expanded: kpiGroup.expanded,
    });

    kpiGroup.childrenIds.forEach((kpiEntryId) => {
      const kpiEntry = kpiEntriesById[kpiEntryId];
      const kpiEntryPath = `${kpiEntryId}-${kpiEntry.name}`;

      const calculations = kpiCalculations(kpiEntry, kpiPeriods, groupRows);
      rows.push({
        id: kpiEntryId,
        name: kpiEntry.name,
        equation: kpiEntry.equation,
        periodDaysParameter: kpiEntry.periodDaysParameter,
        parameterAssignments: kpiEntry.parameterAssignments,
        group: false,
        contentType: ContentType.kpi,
        groupPath: [kpiGroupPath, kpiEntryPath],
        ...calculations,
      });
    });
  });

  return rows;
};

export const kpiRowsSelector = createSelector(
  kpiEntriesByIdSelector,
  kpiGroupsByIdSelector,
  kpiGroupsIdsSelector,
  (state, props) =>
    periodColumnsForKpiCalculationsSelector(state, {
      ...props,
      isGroups: true,
    }),
  groupRowsSelector,
  getKpiRows
);

export const kpiRowsDetailSelector = createSelector(
  kpiRowsSelector,
  (kpiRows) => {
    return kpiRows.reduce((result, kpiRow) => {
      result.push(kpiRow);
      const isKpi = kpiRow.contentType === ContentType.kpi;

      if (!isKpi) {
        return result;
      }

      if (kpiRow.periodDaysParameter) {
        const periodDaysParameterGroupPath = [
          ...kpiRow.groupPath,
          `periodDaysParameter-${kpiRow.periodDaysParameter}`,
        ];

        result.push({
          id: `${kpiRow.id}-periodDaysParameter-${kpiRow.periodDaysParameter}`,
          name: 'Period Days Parameter',
          parameter: kpiRow.periodDaysParameter,
          groupPath: periodDaysParameterGroupPath,
          group: false,
        });
      }

      Object.values(kpiRow.parameterAssignments).forEach(
        (parameterAssignment) => {
          const groupPath = [
            ...kpiRow.groupPath,
            `${parameterAssignment.entryId}-${parameterAssignment.contentDescription}`,
          ];

          result.push({
            ...parameterAssignment,
            id: `${kpiRow.id}-${parameterAssignment.entryId}-${parameterAssignment.contentId}`,
            name: parameterAssignment.contentDescription,
            parameter: parameterAssignment.parameter,
            groupPath,
            group: false,
          });
        }
      );

      return result;
    }, []);
  }
);
